import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { convertCurrency } from "@/lib/currencyConversion";
import { checkAndAwardBadgesForEvent } from "@/lib/badges";
import { getUserProgress } from "@/lib/gamification";
import { cn } from "@/lib/utils";
import { ArrowRight, Check, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { Progress } from "@/components/ui/progress";

interface BudgetStatusSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
}

interface WeekDay {
  label: string;
  hasActivity: boolean;
  isToday: boolean;
}

export const BudgetStatusSheet = ({ open, onOpenChange, userId }: BudgetStatusSheetProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'onTrack' | 'overBudget'>('onTrack');
  const [spent, setSpent] = useState(0);
  const [budget, setBudget] = useState(1000);
  const [currency, setCurrency] = useState('EUR');
  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open) {
      loadBudgetData();
      loadWeekActivity();
      // Log summary_viewed engagement event and check badges
      (async () => {
        try {
          await supabase.from('user_engagement_events').upsert(
            { user_id: userId, event_type: 'summary_viewed', event_date: new Date().toISOString().split('T')[0] },
            { onConflict: 'user_id,event_type,event_date' }
          );
          const progress = await getUserProgress(userId);
          await checkAndAwardBadgesForEvent(userId, 'summary_viewed', progress);
        } catch (e) {
          console.error('Error logging summary_viewed:', e);
        }
      })();
    }
  }, [open, userId]);

  const loadBudgetData = async () => {
    try {
      // Get budget settings
      const { data: budgetData } = await supabase
        .from('budget_settings')
        .select('monthly_budget, currency')
        .eq('user_id', userId)
        .maybeSingle();

      if (budgetData) {
        setBudget(budgetData.monthly_budget);
        setCurrency(budgetData.currency);
      }

      // Get current month expenses with currency for proper conversion
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const firstDayStr = firstDay.toISOString().split('T')[0];

      const userCurrency = budgetData?.currency || 'EUR';

      const { data: expenses } = await supabase
        .from('expenses')
        .select('amount, currency')
        .eq('user_id', userId)
        .gte('expense_date', firstDayStr);

      if (expenses) {
        // Convert all expenses to user's preferred currency
        const totalSpent = expenses.reduce((sum, exp) => {
          const converted = convertCurrency(
            Number(exp.amount),
            exp.currency || 'EUR',
            userCurrency
          );
          return sum + converted;
        }, 0);
        setSpent(totalSpent);
        const remaining = (budgetData?.monthly_budget || 1000) - totalSpent;
        setStatus(remaining >= 0 ? 'onTrack' : 'overBudget');
      }
    } catch (error) {
      console.error('Load budget data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadWeekActivity = async () => {
    const today = new Date();
    const days: WeekDay[] = [];
    const dayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    // Get last 7 days
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayIndex = (date.getDay() + 6) % 7; // Monday = 0

      days.push({
        label: dayLabels[dayIndex],
        hasActivity: false,
        isToday: i === 0,
      });
    }

    // Fetch expenses for last 7 days
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 6);
    const weekAgoStr = weekAgo.toISOString().split('T')[0];

    const { data: expenses } = await supabase
      .from('expenses')
      .select('expense_date')
      .eq('user_id', userId)
      .gte('expense_date', weekAgoStr);

    if (expenses) {
      const activityDates = new Set(expenses.map(e => e.expense_date));
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        days[6 - i].hasActivity = activityDates.has(dateStr);
      }
    }

    setWeekDays(days);
  };

  const handleViewBudget = () => {
    onOpenChange(false);
    navigate('/budget');
  };

  const remaining = budget - spent;
  const progressPercent = Math.min((spent / budget) * 100, 100);
  const isOnTrack = status === 'onTrack';

  const formatCurrency = (amount: number) => {
    const symbols: Record<string, string> = {
      EUR: '€',
      USD: '$',
      GBP: '£',
      UAH: '₴',
      BRL: 'R$',
    };
    return `${symbols[currency] || currency}${Math.abs(amount).toFixed(0)}`;
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="text-center pb-2">
          <div className={cn(
            "mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-2",
            isOnTrack 
              ? "bg-primary/10" 
              : "bg-amber-100 dark:bg-amber-900/20"
          )}>
            {isOnTrack ? (
              <Check className="w-6 h-6 text-primary" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            )}
          </div>
          <DrawerTitle className={cn(
            "text-xl",
            isOnTrack ? "text-primary" : "text-amber-600 dark:text-amber-400"
          )}>
            {isOnTrack ? t('status.onTrack') : t('status.overBudget')}
          </DrawerTitle>
          <DrawerDescription>
            {isOnTrack ? t('status.tooltipOnTrack') : t('status.tooltipOverBudget')}
          </DrawerDescription>
        </DrawerHeader>

        <div className="px-6 pb-6 space-y-6">
          {/* Weekly Tracking Streak - Clarifies these are tracking days, not spending goals */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">{t('budget.trackingStreak') || 'Tracking Streak'}</p>
              <p className="text-xs text-muted-foreground">
                {weekDays.filter(d => d.hasActivity).length}/7 {t('budget.daysTracked') || 'days tracked'}
              </p>
            </div>
            <div className="flex justify-between gap-1">
              {weekDays.map((day, idx) => (
                <div key={idx} className="flex flex-col items-center gap-1">
                  <span className="text-xs text-muted-foreground">{day.label}</span>
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors",
                    day.hasActivity 
                      ? "bg-primary text-primary-foreground" 
                      : "bg-muted text-muted-foreground",
                    day.isToday && !day.hasActivity && "ring-2 ring-primary ring-offset-2 ring-offset-background"
                  )}>
                    {day.hasActivity ? '✓' : '○'}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Budget Progress */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm font-medium text-muted-foreground">{t('nav.budget')}</p>
              <p className="text-sm text-muted-foreground">
                {formatCurrency(spent)} / {formatCurrency(budget)}
              </p>
            </div>
            <Progress 
              value={progressPercent} 
              className={cn(
                "h-3",
                !isOnTrack && "[&>div]:bg-amber-500"
              )}
            />
            <p className={cn(
              "text-lg font-semibold text-center",
              isOnTrack ? "text-primary" : "text-amber-600 dark:text-amber-400"
            )}>
              {remaining >= 0 
                ? `${formatCurrency(remaining)} ${t('budget.remaining')}`
                : `${formatCurrency(remaining)} ${t('status.overBudget')}`
              }
            </p>
          </div>

          {/* View Full Budget Button */}
          <Button 
            variant="outline" 
            className="w-full" 
            onClick={handleViewBudget}
          >
            {t('budget.viewFullBudget')}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
